import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** Gap kept between the panel and the window's edges. */
const GAP_PX = 8;
/** Gap between the « ⋯ » button and the panel. */
const OFFSET_PX = 4;

export interface MenuPlacement {
  left: number;
  top: number;
  side: 'below' | 'above';
}

/**
 * Where the panel goes: under the button, flush with its right or left edge,
 * flipped above it when the window's bottom lacks the room, then kept inside
 * the window. Pure, so it is tested without a layout engine.
 */
export function placeMenu(
  anchor: { left: number; right: number; top: number; bottom: number },
  menu: { width: number; height: number },
  viewport: { width: number; height: number },
  align: 'left' | 'right',
): MenuPlacement {
  const rawLeft = align === 'right' ? anchor.right - menu.width : anchor.left;
  const left = Math.max(GAP_PX, Math.min(rawLeft, viewport.width - menu.width - GAP_PX));
  const below = anchor.bottom + OFFSET_PX;
  const above = anchor.top - OFFSET_PX - menu.height;
  const fitsBelow = below + menu.height + GAP_PX <= viewport.height;
  const fitsAbove = above >= GAP_PX;
  const side = fitsBelow || !fitsAbove ? 'below' : 'above';
  const rawTop = side === 'below' ? below : above;
  const top = Math.max(GAP_PX, Math.min(rawTop, viewport.height - menu.height - GAP_PX));
  return { left, top, side };
}

export interface DrawerMenuPanelProps {
  /** The « ⋯ » button the panel hangs from. */
  anchor: HTMLElement;
  align: 'left' | 'right';
  /** Escape, a click elsewhere, a scroll, a resize, or the anchor leaving the page. */
  onClose: () => void;
  children: ReactNode;
}

/**
 * The panel of a « ⋯ » menu (issue #23): rendered in a portal on `document.body`
 * and placed with `position: fixed` from the button's rectangle, because the
 * grid's box clips its overflow and every later row paints over the cell the
 * menu opens from. Independent of the size of that cell.
 *
 * It closes on Escape, on a click anywhere else (the button decides for
 * itself, it is not « elsewhere »), on any scroll that does not start inside
 * the panel (the grid's box scrolls, the page too), and on a resize.
 *
 * The text size is the button's, so the zoom step of the grid still sizes the menu.
 *
 * Focus: the first item takes it on opening, since the portal puts the panel
 * out of the button's tab order; Escape and Tab give it back to the button.
 */
export function DrawerMenuPanel({ anchor, align, onClose, children }: DrawerMenuPanelProps) {
  const ownRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<MenuPlacement | null>(null);
  // The items size themselves in "em", from the grid's zoom step: the portal leaves that
  // cascade, so the text size is carried over from the button the panel hangs from.
  const [textSize] = useState(() => {
    const { fontSize, lineHeight } = getComputedStyle(anchor);
    // The line height goes over as the ratio the zoom class gives, since the items scale it with their own size.
    const ratio = Number.parseFloat(lineHeight) / Number.parseFloat(fontSize);
    return Number.isFinite(ratio) ? { fontSize, lineHeight: ratio } : { fontSize };
  });
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Measured once before being shown, so it never flashes at the corner.
  useLayoutEffect(() => {
    const panel = ownRef.current;
    if (!panel) return;
    if (!anchor.isConnected) {
      onCloseRef.current();
      return;
    }
    const rect = anchor.getBoundingClientRect();
    setPlacement(
      placeMenu(
        { left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom },
        { width: panel.offsetWidth, height: panel.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight },
        align,
      ),
    );
  }, [anchor, align]);

  useEffect(() => {
    ownRef.current?.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    function handleDocumentClick(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (ownRef.current?.contains(target) || anchor.contains(target)) return;
      onCloseRef.current();
    }
    // Escape pressed with the focus outside the panel; inside, the panel's own handler takes it.
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onCloseRef.current();
    }
    function handleScroll(event: Event) {
      const target = event.target;
      if (target instanceof Node && ownRef.current?.contains(target)) return;
      onCloseRef.current();
    }
    function handleResize() {
      onCloseRef.current();
    }
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keydown', handleKeyDown);
    // Capture: a scroll does not bubble, and the grid's own box is what scrolls.
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [anchor]);

  return createPortal(
    <div
      ref={ownRef}
      data-testid="drawer-menu"
      data-side={placement?.side}
      // The panel is in the React tree of the cell it opens from, so its events still bubble
      // there: the cell handles Enter and Space itself, which would swallow the items' click.
      // Stopping them also hides them from the document, hence Escape is handled here too.
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key !== 'Tab' && event.key !== 'Escape') return;
        anchor.focus({ preventScroll: true });
        onCloseRef.current();
      }}
      onClick={(event) => event.stopPropagation()}
      style={{
        ...textSize,
        left: placement?.left ?? 0,
        top: placement?.top ?? 0,
        // Transparent rather than `visibility: hidden`, which would refuse the focus.
        opacity: placement ? 1 : 0,
      }}
      className="fixed z-50 min-w-56 rounded-md border border-gray-200 bg-white py-1 text-noir shadow-md"
    >
      {children}
    </div>,
    document.body,
  );
}
